import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Home, LogIn, UserPlus, Info, Phone, FileText, AlertTriangle } from 'lucide-react';

const PublicLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appConfig, setAppConfig] = useState({
    app_name: 'Incident Management',
    maintenance_mode: false,
    maintenance_message: 'System is under maintenance. Please try again later.',
    registration_enabled: true
  });
  const location = useLocation();
  
  const appName = appConfig.app_name || process.env.REACT_APP_APP_NAME || 'Incident Management';

  // Fetch public configuration
  useEffect(() => {
    const fetchPublicConfig = async () => {
      try {
        const response = await fetch('/api/config/public');
        if (response.ok) {
          const data = await response.json();
          if (data.data) {
            setAppConfig(data.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch public config:', error);
      }
    };

    fetchPublicConfig();
  }, []);

  // Navigation links
  const navigationLinks = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'About', href: '/about', icon: Info },
    { name: 'Features', href: '/features', icon: FileText },
    { name: 'Contact', href: '/contact', icon: Phone },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 via-white to-primary-100">
      {/* Maintenance Mode Banner */}
      {appConfig.maintenance_mode && (
        <div className="bg-yellow-500 text-white px-4 py-3 text-center relative">
          <div className="flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span className="font-medium">
              {appConfig.maintenance_message}
            </span>
          </div>
        </div>
      )}

      {/* Header/Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center group">
                <div className="relative">
                  <svg
                    className="h-8 w-8 sm:h-10 sm:w-10 text-primary-600 group-hover:text-primary-700 transition-colors"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  <div className="absolute inset-0 bg-primary-600 opacity-0 group-hover:opacity-10 rounded-full transition-opacity" />
                </div>
                <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-bold text-gray-900 group-hover:text-primary-700 transition-colors">
                  <span className="hidden sm:inline">{appName}</span>
                  <span className="sm:hidden">IMS</span>
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigationLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`
                    px-3 py-2 rounded-md text-sm font-medium transition-all
                    ${isActive(link.href)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                    }
                  `}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Auth Buttons - Desktop */}
            <div className="hidden md:flex items-center space-x-3">
              <Link
                to="/login"
                className="flex items-center text-gray-700 hover:text-primary-600 px-4 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-50"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login
              </Link>
              {appConfig.registration_enabled && (
                <Link
                  to="/register"
                  className="flex items-center bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-all hover:shadow-lg"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Register
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 px-3 py-2 text-sm font-medium"
              >
                Login
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`
            md:hidden overflow-hidden transition-all duration-300 ease-in-out
            ${mobileMenuOpen ? 'max-h-96 border-t border-gray-200' : 'max-h-0'}
          `}
        >
          <div className="px-4 pt-2 pb-3 space-y-1 bg-white shadow-lg">
            {navigationLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`
                    flex items-center px-3 py-2 rounded-md text-base font-medium transition-colors
                    ${isActive(link.href)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                    }
                  `}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {link.name}
                  {isActive(link.href) && (
                    <ChevronRight className="h-4 w-4 ml-auto" />
                  )}
                </Link>
              );
            })}
            
            {appConfig.registration_enabled && (
              <div className="pt-3 mt-3 border-t border-gray-200">
                <Link
                  to="/register"
                  className="flex items-center justify-center w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-md text-base font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <UserPlus className="h-5 w-5 mr-2" />
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        <div className="w-full">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Footer Content - Desktop */}
          <div className="hidden sm:block py-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center mb-4">
                  <svg
                    className="h-8 w-8 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  <span className="ml-2 text-lg font-bold text-gray-900">
                    {appName}
                  </span>
                </div>
                <p className="text-gray-600 text-sm max-w-md">
                  Streamline your incident management process with our comprehensive solution. 
                  Track, manage, and resolve issues efficiently.
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Links</h3>
                <ul className="space-y-2">
                  {navigationLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/privacy" className="text-sm text-gray-600 hover:text-primary-600 transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms" className="text-sm text-gray-600 hover:text-primary-600 transition-colors">
                      Terms of Service
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="py-4 sm:py-6 border-t border-gray-200 sm:border-0">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
              <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                © {new Date().getFullYear()} {appName}. All rights reserved.
              </p>
              
              {/* Mobile-only compact links */}
              <div className="flex sm:hidden space-x-4">
                <Link to="/privacy" className="text-xs text-gray-500 hover:text-primary-600">
                  Privacy
                </Link>
                <Link to="/terms" className="text-xs text-gray-500 hover:text-primary-600">
                  Terms
                </Link>
              </div>

              {/* Social Links - Desktop */}
              <div className="hidden sm:flex space-x-6">
                <button
                  onClick={() => window.open('https://twitter.com', '_blank')}
                  className="text-gray-400 hover:text-gray-500"
                  aria-label="Twitter"
                >
                  <span className="sr-only">Twitter</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </button>
                <button
                  onClick={() => window.open('https://github.com', '_blank')}
                  className="text-gray-400 hover:text-gray-500"
                  aria-label="GitHub"
                >
                  <span className="sr-only">GitHub</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;